import { motion } from "framer-motion";
import {
  Broom,
  CookingPot,
  Baby,
  SprayBottle,
  HandHeart,
  Heartbeat,
  ShieldCheck,
  SteeringWheel,
  UsersThree,
  Stethoscope,
  Coffee,
  ArrowUpRight,
} from "@phosphor-icons/react";

const IMG = {
  maid: "/services/housemaid.png",
  nanny: "/services/nanny.png",
  chef: "/services/cook.png",
  security: "/services/watchman.png",
  driver: "/services/driver.png",
  babysitter: "/services/babysitter.png",
  cleaner: "/services/cleaner.png",
  caretaker: "/services/caretaker.png",
  japa: "/services/japa.png",
  patient: "/services/patient.png",
  officeboy: "/services/officeboy.png",
};

const services = [
  { name: "House Maid",             desc: "Full-time & part-time — cleaning, dusting, mopping, utensils.",    Icon: Broom,          span: "md:col-span-5", image: IMG.maid, tall: true },
  { name: "Cook",                   desc: "Home-style meals prepared to your family's taste.",                Icon: CookingPot,     span: "md:col-span-4", image: IMG.chef },
  { name: "Baby Care / Babysitter", desc: "Caring, attentive supervision for your little ones.",              Icon: Baby,           span: "md:col-span-3", image: IMG.babysitter },
  { name: "Elder Care / Caretaker", desc: "Compassionate day-and-night care for elders at home.",             Icon: HandHeart,      span: "md:col-span-4", image: IMG.caretaker },
  { name: "Patient Care",           desc: "Trained attendants for medical & recovery support at home.",       Icon: Stethoscope,    span: "md:col-span-4", image: IMG.patient },
  { name: "Japa Maid",              desc: "Newborn & mother care — specialised post-natal support.",          Icon: Heartbeat,      span: "md:col-span-4", image: IMG.japa },
  { name: "House Cleaning Staff",   desc: "Deep cleaning for a truly spotless home.",                         Icon: SprayBottle,    span: "md:col-span-4", image: IMG.cleaner },
  { name: "Driver",                 desc: "Verified, courteous personal drivers.",                            Icon: SteeringWheel,  span: "md:col-span-4", image: IMG.driver },
  { name: "Watchman",               desc: "Reliable security for your home or premises.",                     Icon: ShieldCheck,    span: "md:col-span-4", image: IMG.security },
  { name: "Office Boy / Staff",     desc: "Well-mannered office boys & support staff for your workplace.",    Icon: Coffee,         span: "md:col-span-4", image: IMG.officeboy },
];

function Card({ s, onBook, i }) {
  const withImage = !!s.image;
  return (
    <motion.button
      onClick={() => onBook(s.name)}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative col-span-1 ${s.span} text-left rounded-3xl overflow-hidden border border-slate-200 bg-white ${
        s.tall ? "md:row-span-2" : ""
      } hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300`}
      data-testid={`service-card-${s.name.toLowerCase().split(" ")[0]}`}
    >
      {withImage && (
        <div className={`relative overflow-hidden ${s.tall ? "h-72 md:h-[60%]" : "h-40"}`}>
          <img
            src={s.image}
            alt={s.name}
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/50 to-transparent" />
        </div>
      )}
      <div className="p-6 md:p-7">
        <div className="flex items-start justify-between">
          <span className="grid place-items-center h-12 w-12 rounded-2xl bg-[var(--navy)]/8 text-[var(--navy)] group-hover:bg-[var(--gold)] group-hover:text-[var(--navy)] transition-colors">
            <s.Icon weight="duotone" size={26} />
          </span>
          <ArrowUpRight
            size={22}
            className="text-slate-300 group-hover:text-[var(--navy)] group-hover:rotate-45 transition-[transform,color] duration-300"
          />
        </div>
        <h3 className="font-display text-2xl text-[var(--navy)] mt-5 tracking-tight">
          {s.name}
        </h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
      </div>
    </motion.button>
  );
}

export default function Services({ onBook }) {
  return (
    <section id="services" className="py-24 md:py-32 bg-white" data-testid="services">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl mb-14">
          <p className="label-caps text-[var(--gold)] mb-4">Our Services</p>
          <h2 className="font-display text-4xl md:text-6xl text-[var(--navy)] tracking-tighter leading-[1.02]">
            Everything your home needs, one trusted call away.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 md:auto-rows-[minmax(0,1fr)]">
          {services.map((s, i) => (
            <Card key={s.name} s={s} onBook={onBook} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
